/**
 * Humanizer Studio - Main Application
 *
 * Distraction-free workspace with:
 * - 3-mode theme system (light, sepia, dark) at the foundation
 * - Auto-hiding topbar (hides after 3s of no mouse movement)
 * - Floating corner assistant for AUI access
 * - Admin interface for system management
 */

import { useEffect, useState, useCallback } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, useNavigate } from 'react-router-dom';

// Use HashRouter for Electron (file:// protocol), BrowserRouter for web
const Router = typeof window !== 'undefined' && window.isElectron ? HashRouter : BrowserRouter;
import { ApiProvider, useApi } from './contexts/ApiContext';
import { AuthProvider, useAuth, useIsAdmin } from './contexts/AuthContext';
import { ArchiveProvider } from './contexts/ArchiveContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { BufferSyncProvider, useBufferSync } from './contexts/BufferSyncContext';
import { PanelProvider, usePanels, usePanelState } from './contexts/PanelContext';
import { ToastProvider } from './contexts/ToastContext';
import { ToastContainer } from './components/ui/Toast';
import { MainWorkspace, type WorkspaceContent, type SplitContent, type WorkspaceMode, MediaWorkspace } from './components/workspace';
import { MediaProvider } from './contexts/MediaContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { BookProvider } from './contexts/BookContext';
import { StackProvider } from './contexts/StackContext';
import { PlatformProvider } from './contexts/PlatformContext';
import { OnboardingWizard } from './components/onboarding/OnboardingWizard';
import { VettingToastListener } from './components/ui/VettingToastListener';
import { TranscriptionProgressListener } from './components/ui/TranscriptionProgressListener';
import { LlmErrorToastListener } from './components/ui/LlmErrorToastListener';
import { ArchivePane } from './components/archive/ArchivePane';
import { ToolsPane } from './components/tools/ToolsPane';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { CornerAssistant } from './components/CornerAssistant';
import { LoginModal, UserMenu } from './components/auth';
import { CloudReauthModal } from './components/auth/CloudReauthModal';
import { AdminLayout, AdminDashboard, AdminUsers, AdminPrompts, AdminModels, AdminTiers, AdminProviders, AdminFeatures, AdminApiKeys, AdminUsage, AdminCosts, AdminPricing, AdminSubscriptions, AdminAudit, AdminAgents, ElectronAdminDashboard, ElectronAdminStatus } from './components/admin';
import { LocalUsageDashboard } from './components/settings/LocalUsageDashboard';
import { ByokCostAlerts } from './components/settings/ByokCostAlerts';
import { CloudQuotaStatus } from './components/settings/CloudQuotaStatus';
import { LocalPromptEditor } from './components/settings/LocalPromptEditor';
import { SettingsLayout, SettingsProfile, SettingsApiKeys, SettingsUsage, SettingsPreferences, SettingsPrompts, SettingsLocalProfile, SettingsCloudAccount, SettingsByok, SettingsModels, SettingsEmbeddings } from './components/settings';
import type { SearchResult } from './contexts/ApiContext';
import type { SentenceStepResult, SentencingV2Result, NoemaGraphResult, SicResult, ChapterGraphResult } from './types/analysis';
import type { FindBuffer } from './components/archive/CuratorView';
import useArchiveApi from './hooks/useArchiveApi';
import { createLogger } from './utils/debug';
import { extractPointerFromSrc } from './utils/media-url';

const log = createLogger('App');

// ═══════════════════════════════════════════════════════════════════════════
// STUDIO CONTENT - Main application layout
// ═══════════════════════════════════════════════════════════════════════════

function StudioContent() {
  const api = useApi();
  const archiveApi = useArchiveApi();
  const { theme, setTheme } = useTheme();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  // Topbar always visible (removed auto-hide for now)
  const topbarVisible = true;
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Panel state for 3-panel layout
  const { state: panelState, toggleArchive, toggleTools } = usePanels();

  // Use BufferSync for session and content management
  const {
    sessionId,
    isConnected,
    workingContent,
    importArchiveNode,
    openNodeInContext,
    pendingHighlight,
    pendingMessageIndex,
    threadIndex,
    goToPrevThread,
    goToNextThread,
    clearPendingNavigation,
  } = useBufferSync();

  // Keyboard shortcuts for panel toggling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Tab: Toggle focus between panels
      if (e.key === 'Tab' && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
        // Let default Tab behavior work for accessibility
        return;
      }
      // Cmd+[ or Ctrl+[: Toggle archive panel
      if ((e.metaKey || e.ctrlKey) && e.key === '[') {
        e.preventDefault();
        toggleArchive();
      }
      // Cmd+] or Ctrl+]: Toggle tools panel
      if ((e.metaKey || e.ctrlKey) && e.key === ']') {
        e.preventDefault();
        toggleTools();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleArchive, toggleTools]);

  // Detect thread mode: multiple items with threadId metadata
  const isThreadMode = workingContent.length > 1 &&
    workingContent[0]?.metadata.source?.threadId !== undefined;

  // Convert buffer content to workspace content for display
  const content: WorkspaceContent | null = workingContent.length > 0
    ? {
        id: workingContent[0].id,
        title: workingContent[0].metadata.source?.threadTitle ??
               workingContent[0].metadata.source?.path?.[0] ??
               'Content',
        text: isThreadMode
          ? workingContent[0].text  // In thread mode, text is per-message (set by MainWorkspace)
          : workingContent.map(item => item.text).join('\n\n'),
        source: {
          type: workingContent[0].metadata.source?.type ?? 'buffer',
          path: workingContent[0].metadata.source?.path ?? ['Workspace'],
        },
        metadata: {
          wordCount: workingContent.reduce((sum, item) =>
            sum + (item.metadata.wordCount ?? 0), 0),
          authorRole: workingContent[0].metadata.authorRole,
          timestamp: workingContent[0].metadata.timestamp,
        },
      }
    : null;

  // Topbar auto-hide disabled - keeping it always visible for now
  // Can be re-enabled later with user preference

  // Handle result selection from CornerAssistant
  // Now uses buffer system instead of local state
  const handleSelectResult = useCallback((result: SearchResult) => {
    importArchiveNode({
      id: result.id,
      text: result.text,
      type: 'search-result',
      sourceType: result.provenance?.sourceType || result.source,
      threadId: result.provenance?.threadRootId,
      threadTitle: result.provenance?.threadTitle,
      authorRole: result.provenance?.authorRole,
      wordCount: result.wordCount,
    });
  }, [importArchiveNode]);

  // Handle "Open in Workspace" from transcription panel in MediaBrowser
  const handleOpenTranscriptionInWorkspace = useCallback((text: string, modelLabel: string) => {
    importArchiveNode({
      id: crypto.randomUUID(),
      text: `**Transcription** _(${modelLabel})_\n\n${text}`,
      type: 'transcription',
      sourceType: 'transcription',
    });
  }, [importArchiveNode]);

  // Navigate to specific message index (from search thread loading)
  const [navigateToMessageIndex, setNavigateToMessageIndex] = useState<number | undefined>(undefined);

  const handleNavigateToMessage = useCallback((index: number) => {
    setNavigateToMessageIndex(index);
  }, []);

  // Highlight text from search-as-harvest
  const [highlightText, setHighlightText] = useState<string | null>(null);

  const handleHighlightRequested = useCallback((text: string) => {
    setHighlightText(text);
  }, []);

  // Sentencing data for workspace overlays (v1 tetralemma + v2 role classifier — mutually exclusive)
  const [sentencingData, setSentencingData] = useState<SentenceStepResult['step'][]>([]);
  const [roleClassifierData, setRoleClassifierData] = useState<SentencingV2Result | null>(null);

  const handleSentencingComplete = useCallback((steps: SentenceStepResult['step'][]) => {
    setSentencingData(steps);
    if (steps.length > 0) setRoleClassifierData(null); // mutual exclusivity
  }, []);

  const handleRoleClassifierComplete = useCallback((data: SentencingV2Result | null) => {
    setRoleClassifierData(data);
    if (data) setSentencingData([]); // mutual exclusivity
  }, []);

  // Noema graph data for draft generation intentional brief
  const [noemaGraphData, setNoemaGraphData] = useState<NoemaGraphResult | null>(null);

  const handleNoemaGraphComplete = useCallback((data: NoemaGraphResult | null) => {
    setNoemaGraphData(data);
  }, []);

  // SIC data for transformation guidance (Phase 3A: wired, Phase 3B: consumed)
  const [sicData, setSicData] = useState<SicResult | null>(null);

  const handleSicComplete = useCallback((data: SicResult | null) => {
    setSicData(data);
  }, []);

  // Chapter graph data for cross-chapter analysis
  const [chapterGraphData, setChapterGraphData] = useState<ChapterGraphResult | null>(null);

  const handleChapterGraphComplete = useCallback((data: ChapterGraphResult | null) => {
    setChapterGraphData(data);
  }, []);

  // M5: Curator search reopen from ProjectBrowser
  const [curatorFindBuffer, setCuratorFindBuffer] = useState<FindBuffer | undefined>(undefined);

  // View mode request from ArchivePane (chapter click)
  const [requestedViewMode, setRequestedViewMode] = useState<'stack' | 'outline' | 'draft' | null>(null);

  // Workspace mode request (archive vs project) from ArchivePane navigation
  const [requestedWorkspaceMode, setRequestedWorkspaceMode] = useState<WorkspaceMode | null>(null);

  // Current view mode reported by MainWorkspace (for sibling components)
  const [currentViewMode, setCurrentViewMode] = useState<string>('read');

  // Split-pane state for before/after comparison
  const [splitContent, setSplitContent] = useState<SplitContent | null>(null);

  const handleSplitRequested = useCallback((content: SplitContent) => {
    setSplitContent(content);
  }, []);

  const handleSplitDismissed = useCallback(() => {
    setSplitContent(null);
  }, []);

  // Handle conversation navigation from media detail links
  const { setArchiveSearch, setArchiveTab, setPendingMediaId } = usePanels();

  const handleConversationClick = useCallback((conversationId: string) => {
    // Open the conversation thread in the workspace via BufferSync
    // (fetches full thread, imports content, sets message index)
    openNodeInContext({ nodeId: conversationId });
  }, [openNodeInContext]);

  // Handle media image clicks in workspace — navigate to gallery with item selected
  const handleMediaClick = useCallback(async (src: string) => {
    try {
      const pointer = extractPointerFromSrc(src);
      if (pointer) {
        const result = await api.media.byPointer(pointer);
        if (result.found && result.media) {
          setPendingMediaId(result.media.id);
          return;
        }
      }
    } catch (err) {
      log.error('Failed to look up media:', err);
    }
  }, [api, setPendingMediaId]);

  // Handle "Search in thread" from workspace — routes through archiveApi
  // for Electron local DB support (cloud api.search needs sessionId which is null locally)
  const handleSearchInThread = useCallback(async (query: string, threadId: string) => {
    try {
      const response = await archiveApi.search('', query, { limit: 50 });
      // Filter results to only those from this thread
      const threadResults = response.results.filter(
        (r) => r.provenance?.threadRootId === threadId
      );
      setArchiveSearch(query, threadResults as unknown as SearchResult[]);
    } catch (err) {
      log.error('Search in thread failed:', err);
    }
  }, [archiveApi, setArchiveSearch]);

  // Handle "Find Similar" from workspace — uses archiveApi for Electron+cloud routing
  const handleFindSimilar = useCallback(async (text: string) => {
    try {
      const response = await archiveApi.findSimilar(text.slice(0, 500), { limit: 10, threshold: 0.3 });
      setArchiveSearch(
        text.slice(0, 80) + (text.length > 80 ? '...' : ''),
        response.results as unknown as SearchResult[]
      );
    } catch (err) {
      log.error('Find similar failed:', err);
    }
  }, [archiveApi, setArchiveSearch]);

  return (
    <div className="studio">
      {/* Skip to content link (WCAG 2.1 AA) */}
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      {/* Auto-hiding topbar */}
      <header className={`studio-topbar ${topbarVisible ? '' : 'studio-topbar--hidden'}`}>
        <div className="studio-topbar__left">
          {/* Mobile: Archive toggle */}
          <button
            className="studio-topbar__mobile-toggle"
            onClick={toggleArchive}
            aria-label={panelState.archiveOpen ? 'Close archive' : 'Open archive'}
            title="Archive"
          >
            &#9776;
          </button>
          <span className="studio-topbar__logo">humanizer</span>
        </div>

        <div className="studio-topbar__center">
          <span className="studio-topbar__title">Studio</span>
        </div>

        <div className="studio-topbar__right">
          {/* Settings link for authenticated users */}
          {isAuthenticated && (
            <button
              className="btn btn--ghost btn--sm studio-topbar__desktop-only"
              onClick={() => navigate('/settings')}
            >
              Settings
            </button>
          )}

          {/* Admin link for admin users */}
          {isAdmin && (
            <button
              className="btn btn--ghost btn--sm studio-topbar__desktop-only"
              onClick={() => navigate('/admin')}
            >
              Admin
            </button>
          )}

          {/* Theme toggle */}
          <div className="theme-toggle" role="radiogroup" aria-label="Theme">
            <button
              className={`theme-toggle__btn ${theme === 'light' ? 'theme-toggle__btn--active' : ''}`}
              onClick={() => setTheme('light')}
              title="Light"
              aria-label="Light theme"
            >
              &#9728;
            </button>
            <button
              className={`theme-toggle__btn ${theme === 'sepia' ? 'theme-toggle__btn--active' : ''}`}
              onClick={() => setTheme('sepia')}
              title="Sepia"
              aria-label="Sepia theme"
            >
              &#9788;
            </button>
            <button
              className={`theme-toggle__btn ${theme === 'dark' ? 'theme-toggle__btn--active' : ''}`}
              onClick={() => setTheme('dark')}
              title="Dark"
              aria-label="Dark theme"
            >
              &#9790;
            </button>
          </div>

          {/* Auth: User menu or login button */}
          {authLoading ? (
            <span className="auth-loading">
              <span className="auth-loading__spinner" />
            </span>
          ) : isAuthenticated && user ? (
            <UserMenu user={user} />
          ) : (
            <button
              className="btn btn--secondary btn--sm"
              onClick={() => setShowLoginModal(true)}
            >
              Sign In
            </button>
          )}

          {/* Mobile: Tools toggle */}
          <button
            className="studio-topbar__mobile-toggle"
            onClick={toggleTools}
            aria-label={panelState.toolsOpen ? 'Close tools' : 'Open tools'}
            title="Tools"
          >
            &#9881;
          </button>
        </div>
      </header>

      {/* 3-Panel Layout: Archive | Workspace | Tools */}
      <div
        className={`studio-layout ${
          !panelState.archiveOpen && !panelState.toolsOpen
            ? 'studio-layout--both-collapsed'
            : !panelState.archiveOpen
            ? 'studio-layout--archive-collapsed'
            : !panelState.toolsOpen
            ? 'studio-layout--tools-collapsed'
            : ''
        }`}
      >
        {/* Archive Panel (Left) */}
        <div className={`panel panel--archive ${!panelState.archiveOpen ? 'panel--collapsed' : 'panel--mobile-open'}`}>
          <ErrorBoundary fallback="panel" label="Archive panel">
            <ArchivePane onHighlightRequested={handleHighlightRequested} onNavigateToMessage={handleNavigateToMessage} onNavigateToConversation={handleConversationClick} onCuratorSearchOpen={setCuratorFindBuffer} onViewModeRequest={setRequestedViewMode} onOpenTranscriptionInWorkspace={handleOpenTranscriptionInWorkspace} />
          </ErrorBoundary>
        </div>

        {/* Main Workspace (Center) — swap to MediaWorkspace when active */}
        <div className="panel panel--workspace" id="main-content" role="main">
          <ToastContainer />
          <ErrorBoundary fallback="panel" label="Workspace">
            {panelState.mediaWorkspaceActive ? (
              <MediaWorkspace />
            ) : (
              <MainWorkspace
                content={content}
                messages={isThreadMode ? workingContent : undefined}
                onFindSimilar={handleFindSimilar}
                splitContent={splitContent}
                onSplitDismissed={handleSplitDismissed}
                highlightText={pendingHighlight ?? highlightText}
                sentencingData={sentencingData}
                roleClassifierData={roleClassifierData}
                initialMessageIndex={pendingMessageIndex ?? navigateToMessageIndex}
                onSearchInThread={handleSearchInThread}
                onMediaClick={handleMediaClick}
                onConversationClick={handleConversationClick}
                threadIndex={threadIndex}
                onPrevThread={goToPrevThread}
                onNextThread={goToNextThread}
                onClearPendingNavigation={clearPendingNavigation}
                curatorFindBuffer={curatorFindBuffer}
                onCuratorFindBufferConsumed={() => setCuratorFindBuffer(undefined)}
                requestedViewMode={requestedViewMode}
                onRequestedViewModeConsumed={() => setRequestedViewMode(null)}
                requestedWorkspaceMode={requestedWorkspaceMode}
                onRequestedWorkspaceModeConsumed={() => setRequestedWorkspaceMode(null)}
                onViewModeChange={setCurrentViewMode}
                noemaGraphData={noemaGraphData}
                sicData={sicData}
                chapterGraphData={chapterGraphData}
              />
            )}
          </ErrorBoundary>
        </div>

        {/* Tools Panel (Right) */}
        <div className={`panel panel--tools ${!panelState.toolsOpen ? 'panel--collapsed' : 'panel--mobile-open'}`}>
          <ErrorBoundary fallback="panel" label="Tools panel">
            <ToolsPane onSplitRequested={handleSplitRequested} onSentencingComplete={handleSentencingComplete} onRoleClassifierComplete={handleRoleClassifierComplete} onNoemaGraphComplete={handleNoemaGraphComplete} onSicComplete={handleSicComplete} onChapterGraphComplete={handleChapterGraphComplete} viewMode={currentViewMode} sicData={sicData} />
          </ErrorBoundary>
        </div>

        {/* Mobile backdrop when a panel is open */}
        {(panelState.archiveOpen || panelState.toolsOpen) && (
          <div
            className="panel-backdrop panel-backdrop--visible"
            onClick={() => {
              if (panelState.archiveOpen) toggleArchive();
              if (panelState.toolsOpen) toggleTools();
            }}
            aria-hidden="true"
          />
        )}
      </div>

      {/* Edge toggle buttons for collapsed panels */}
      {!panelState.archiveOpen && (
        <button
          className="panel-toggle panel-toggle--archive"
          onClick={toggleArchive}
          aria-label="Open archive panel"
          title="Open Archive (Cmd+[)"
        >
          ›
        </button>
      )}
      {!panelState.toolsOpen && (
        <button
          className="panel-toggle panel-toggle--tools"
          onClick={toggleTools}
          aria-label="Open tools panel"
          title="Open Tools (Cmd+])"
        >
          ‹
        </button>
      )}

      {/* Floating corner assistant for AUI */}
      <CornerAssistant
        onSelectResult={handleSelectResult}
        sessionId={sessionId ?? undefined}
        isConnected={isConnected}
        viewMode={currentViewMode}
      />

      {/* Login modal */}
      {showLoginModal && (
        <LoginModal onClose={() => setShowLoginModal(false)} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ADMIN PLACEHOLDER PAGES
// These will be expanded into full components
// ═══════════════════════════════════════════════════════════════════════════

function AdminPlaceholder({ title }: { title: string }) {
  return (
    <div className="admin-section">
      <div className="admin-section__header">
        <h2 className="admin-section__title">{title}</h2>
      </div>
      <div className="admin-section__content">
        <div className="admin-empty">
          <span className="admin-empty__icon">🚧</span>
          <h3 className="admin-empty__title">Coming Soon</h3>
          <p className="admin-empty__description">
            This section is under development.
          </p>
        </div>
      </div>
    </div>
  );
}

function AdminStatus() {
  const api = useApi();
  const [status, setStatus] = useState<{ status: string; message?: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.admin.getStatus()
      .then(setStatus)
      .catch(() => setStatus({ status: 'error', message: 'Failed to fetch status' }))
      .finally(() => setLoading(false));
  }, [api.admin]);

  if (loading) {
    return (
      <div className="admin-loading">
        <span className="admin-loading__spinner" />
      </div>
    );
  }

  return (
    <div className="admin-section">
      <div className="admin-section__header">
        <h2 className="admin-section__title">System Status</h2>
      </div>
      <div className="admin-section__content">
        <div className="admin-alert admin-alert--info">
          <span className="admin-alert__icon">ℹ️</span>
          <div className="admin-alert__content">
            <h4 className="admin-alert__title">API Status: {status?.status}</h4>
            <p className="admin-alert__message">{status?.message ?? 'System is operational'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// AdminUsers is now imported from ./components/admin

// AdminApiKeys is now imported from ./components/admin

// AdminTiers is now imported from ./components/admin

// AdminModels is now imported from ./components/admin

// AdminPrompts is now imported from ./components/admin

// AdminProviders is now imported from ./components/admin

// AdminFeatures is now imported from ./components/admin

// AdminSubscriptions is now imported from ./components/admin

// AdminCosts is now imported from ./components/admin

// AdminUsage is now imported from ./components/admin

// AdminAudit is now imported from ./components/admin

// ═══════════════════════════════════════════════════════════════════════════
// APP ROUTES - Router configuration
// ═══════════════════════════════════════════════════════════════════════════

function AppRoutes() {
  return (
    <Routes>
      {/* Main Studio */}
      <Route path="/" element={<StudioContent />} />

      {/* Admin Routes — platform-branched */}
      <Route path="/admin" element={<AdminLayout />}>
        {window.isElectron ? (
          <>
            <Route index element={<ElectronAdminDashboard />} />
            <Route path="status" element={<ElectronAdminStatus />} />
            <Route path="models" element={<SettingsModels />} />
            <Route path="prompts" element={<LocalPromptEditor />} />
            <Route path="analytics/usage" element={<LocalUsageDashboard />} />
            <Route path="costs" element={<ByokCostAlerts />} />
            <Route path="tiers" element={<CloudQuotaStatus />} />
          </>
        ) : (
          <>
            <Route index element={<AdminDashboard />} />
            <Route path="status" element={<AdminStatus />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="api-keys" element={<AdminApiKeys />} />
            <Route path="tiers" element={<AdminTiers />} />
            <Route path="models" element={<AdminModels />} />
            <Route path="prompts" element={<AdminPrompts />} />
            <Route path="providers" element={<AdminProviders />} />
            <Route path="features" element={<AdminFeatures />} />
            <Route path="pricing" element={<AdminPricing />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="costs" element={<AdminCosts />} />
            <Route path="analytics/usage" element={<AdminUsage />} />
            <Route path="agents" element={<AdminAgents />} />
            <Route path="audit" element={<AdminAudit />} />
          </>
        )}
      </Route>

      {/* Settings Routes */}
      <Route path="/settings" element={<SettingsLayout />}>
        <Route index element={window.isElectron ? <SettingsLocalProfile /> : <SettingsProfile />} />
        <Route path="profile" element={<SettingsProfile />} />
        <Route path="local-profile" element={<SettingsLocalProfile />} />
        <Route path="api-keys" element={<SettingsApiKeys />} />
        <Route path="usage" element={<SettingsUsage />} />
        <Route path="preferences" element={<SettingsPreferences />} />
        <Route path="prompts" element={<SettingsPrompts />} />
        <Route path="cloud" element={<SettingsCloudAccount />} />
        <Route path="byok" element={<SettingsByok />} />
        <Route path="models" element={<SettingsModels />} />
        <Route path="embeddings" element={<SettingsEmbeddings />} />
      </Route>
    </Routes>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// APP - Provider wrapper
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Inner app component that has access to ApiContext
 * This allows ArchiveProvider to receive the API client
 */
function AppWithArchive() {
  const api = useApi();

  return (
    <ArchiveProvider apiClient={api}>
      <ProjectProvider>
        <BookProvider>
          <StackProvider>
            <ToastProvider>
              <VettingToastListener />
              <LlmErrorToastListener />
              <CloudReauthModal />
              <BufferSyncProvider>
                <MediaProvider>
                  <TranscriptionProgressListener />
                  <PanelProvider>
                    <AppRoutes />
                  </PanelProvider>
                </MediaProvider>
              </BufferSyncProvider>
            </ToastProvider>
          </StackProvider>
        </BookProvider>
      </ProjectProvider>
    </ArchiveProvider>
  );
}

/**
 * Component that passes auth state to PlatformProvider
 */
function AppWithPlatform() {
  const { isAuthenticated } = useAuth();

  return (
    <PlatformProvider isAuthenticated={isAuthenticated}>
      <ApiProvider>
        <AppWithArchive />
      </ApiProvider>
    </PlatformProvider>
  );
}

export function App() {
  const [showOnboarding, setShowOnboarding] = useState<boolean | null>(null);

  // Check first-run state on mount (Electron only)
  useEffect(() => {
    if (!window.isElectron || !window.electronApp) {
      setShowOnboarding(false);
      return;
    }
    window.electronApp.isFirstRun().then((isFirst) => {
      setShowOnboarding(isFirst);
    }).catch(() => {
      setShowOnboarding(false);
    });
  }, []);

  // Allow re-launching wizard from Settings
  useEffect(() => {
    const handler = () => setShowOnboarding(true);
    window.addEventListener('show-onboarding', handler);
    return () => window.removeEventListener('show-onboarding', handler);
  }, []);

  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <AppWithPlatform />
          {showOnboarding && (
            <OnboardingWizard onComplete={() => setShowOnboarding(false)} />
          )}
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}
